import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { DEFAULT_MESSAGE_TEMPLATES_QUERY_PARAMS } from 'constants/templates'
import { getLocation } from 'ducks/router'
import { useConfirmationModal, useModal } from 'hooks'
import { templates } from 'orm'
import { useMessageTemplateManagementModalForm } from './common/MessageTemplateManagementForm/hooks'

const useMessageTemplateManagementQueries = () => {
  const handleFetchTemplates = React.useCallback(
    ({ limit, offset, ordering, search }) =>
      templates.fetchStart({ limit, offset, ordering, search }),
    [],
  )

  return {
    handleFetchTemplates,
  }
}

const useMessageTemplateManagementMutations = () => {
  const dispatch = useDispatch()

  const handleMessageTemplateUpdate = React.useCallback(
    ({ data, id }, { onFailureAction, onSuccessAction, postSuccessAction }) => {
      dispatch(
        templates.patchStart({
          data,
          pk: id,
          onFailureAction,
          onSuccessAction,
          postSuccessAction,
        }),
      )
    },
    [dispatch],
  )

  return {
    handleMessageTemplateUpdate,
  }
}

export const useMessageTemplateManagementModalState = (state, handlers) => {
  const { template, isOpen } = state
  const { onCancel, onClose, onConfirm } = handlers

  const {
    state: { control, formState, variables, isDirtyOrInvalid },
    handlers: { handleFormSubmit, handleFormReset },
  } = useMessageTemplateManagementModalForm(
    { template, isOpen },
    { onClose, onConfirm },
  )

  const {
    handleConfirmationClose,
    handleConfirmationConfirm,
    handleConfirmationOpen,
    isConfirmationOpen,
  } = useConfirmationModal(() => {
    handleFormReset()
    onClose()
  })

  const handleCancel = React.useCallback(() => {
    isDirtyOrInvalid ? handleConfirmationOpen() : onCancel()
  }, [handleConfirmationOpen, onCancel, isDirtyOrInvalid])

  const handleClose = React.useCallback(() => {
    isDirtyOrInvalid ? handleConfirmationOpen() : onClose()
  }, [handleConfirmationOpen, onClose, isDirtyOrInvalid])

  const handleConfirm = React.useCallback(handleFormSubmit, [handleFormSubmit])

  return {
    state: {
      form: { control, formState },
      variables,
      isConfirmationOpen,
    },
    handlers: {
      handleConfirmationClose,
      handleConfirmationConfirm,
      handleCancel,
      handleClose,
      handleConfirm,
    },
  }
}

export const useMessageTemplateManagementModal = (state, handlers) => {
  const { query } = useSelector(getLocation)

  const { template } = state ?? {}
  const { onResetTemplate } = handlers ?? {}

  const { handleClose, handleOpen, isOpen } = useModal()

  const { handleFetchTemplates } = useMessageTemplateManagementQueries()

  const {
    handleMessageTemplateUpdate,
  } = useMessageTemplateManagementMutations()

  const handleMessageTemplateManagementModalClose = React.useCallback(() => {
    onResetTemplate()
    handleClose()
  }, [handleClose, onResetTemplate])

  const handleConfirm = React.useCallback(
    (data, { onFailureAction, postSuccessAction }) => {
      handleMessageTemplateUpdate(
        { data, id: template?.id },
        {
          onSuccessAction: handleFetchTemplates(
            query ?? DEFAULT_MESSAGE_TEMPLATES_QUERY_PARAMS,
          ),
          onFailureAction,
          postSuccessAction,
        },
      )
    },
    [query, template, handleFetchTemplates, handleMessageTemplateUpdate],
  )

  return {
    handleMessageTemplateManagementModalClose,
    handleMessageTemplateManagementModalConfirm: handleConfirm,
    handleMessageTemplateManagementModalOpen: handleOpen,
    isMessageTemplateManagementModalOpen: isOpen,
  }
}
